// AIForm — extract events from free-form text. Calls the consumer's
// `aiExtract` callback when wired (production: server LLM); falls
// through to the local heuristic regex pass when not, OR when the
// remote call fails. The local fallback path keeps the user surface
// useful in degraded states.

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  Badge,
  Button,
  Empty,
  List,
  Select,
  Tag,
  Textarea,
  type SelectOption,
} from '@viquard/doc-ui/components';
import { IcnAlert } from '@viquard/doc-ui/icons';
import type { CalendarGroup, CalendarKindMap } from '../../types.js';
import {
  resolveKindDefaults,
  type CalendarKindBehaviorMap,
} from '../../utils/kindBehavior.js';
import {
  formatMessage,
  useCalendarMessages,
} from '../../i18n.js';
import {
  buildDefaultGroupOptions,
  PRIVATE_GROUP_SENTINEL,
} from './group-helpers.js';
import {
  KIND_DEFAULT_REMINDER_VALUE,
  buildReminderOptions,
  plusMinutes,
  type DefaultReminderSelection,
} from './helpers.js';
import { FooterActions, Section } from './layout.js';
import { localAIExtract, localAIExtractStructuredSchedule } from './local-ai.js';
import type { EventComposerSubmitDraft } from './types.js';

export interface AIFormProps {
  readonly kindTheme: CalendarKindMap;
  readonly kindBehavior: CalendarKindBehaviorMap;
  readonly groups?: readonly CalendarGroup[] | undefined;
  readonly aiExtract?:
    | ((text: string) => Promise<readonly EventComposerSubmitDraft[]>)
    | undefined;
  readonly onSubmitBatch: (rows: readonly EventComposerSubmitDraft[]) => void;
  readonly onCancel: () => void;
  readonly mobile: boolean;
  readonly viewerTz?: string | undefined;
  readonly recurrenceEnabled: boolean;
  readonly remindersEnabled: boolean;
}

function isSilentAiFallbackError(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { silentAiFallback?: unknown }).silentAiFallback === true
  );
}

export function AIForm(props: AIFormProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    kindTheme,
    kindBehavior,
    groups,
    aiExtract,
    onSubmitBatch,
    onCancel,
    mobile,
    viewerTz,
    recurrenceEnabled,
    remindersEnabled,
  } = props;
  // Seed the textarea with the localised placeholder. Re-seeding when
  // the user switches language mid-flow would clobber their typing, so
  // the seed is captured ONCE at mount via a lazy initial.
  const [text, setText] = useState(() => messages.composerAiPlaceholder);
  // Default to "no group" so AI-extracted events are private until the
  // user explicitly picks a default. Same posture as SingleForm and
  // BatchForm — keeps the safer-default invariant uniform across modes.
  const [defaultGroupId, setDefaultGroupId] = useState<string | null>(null);
  // AI suggestions can preserve a provider-supplied reminder, otherwise
  // this compact default is applied to every accepted suggestion.
  const [defaultReminderMinutes, setDefaultReminderMinutes] =
    useState<DefaultReminderSelection>(KIND_DEFAULT_REMINDER_VALUE);
  const [loading, setLoading] = useState(false);
  const [extracted, setExtracted] = useState<readonly EventComposerSubmitDraft[] | null>(
    null,
  );
  const [removed, setRemoved] = useState<ReadonlySet<number>>(new Set());
  /** Soft error banner — when the upstream AI call fails we still
   *  surface the local heuristic results, but flag that the AI was
   *  skipped so the user knows the suggestions are weaker. */
  const [aiError, setAiError] = useState<string | null>(null);
  /** True when we successfully called the remote AI provider on the
   *  most recent run. Drives the "powered by …" badge. */
  const [usedAi, setUsedAi] = useState(false);

  const kindKeys = useMemo(
    () => Object.keys(kindTheme).filter((k) => k !== 'default'),
    [kindTheme],
  );

  const run = useCallback(async (): Promise<void> => {
    setLoading(true);
    setRemoved(new Set());
    setAiError(null);
    try {
      let rows: readonly EventComposerSubmitDraft[];
      const localOptions = {
        defaultGroupId,
        kindKeys,
        viewerTz,
        kindBehavior,
      };
      const structuredRows = localAIExtractStructuredSchedule(text, localOptions);
      if (structuredRows.length >= 2) {
        setUsedAi(false);
        rows = structuredRows;
      } else {
        const extractLocal = (): readonly EventComposerSubmitDraft[] =>
          localAIExtract(text, localOptions);
        if (aiExtract !== undefined) {
          try {
            const fromAi = await aiExtract(text);
            if (fromAi.length > 0) {
              setUsedAi(true);
              rows = fromAi;
            } else {
              setUsedAi(false);
              rows = extractLocal();
            }
          } catch (err) {
            // Upstream AI failed (provider down, network, missing key).
            // Fall through to the local heuristic extractor so the user
            // surface keeps working. Some consumers mark expected
            // provider fallbacks as silent so a transient backend outage
            // does not become noisy UI.
            const message = err instanceof Error ? err.message : String(err);
            setAiError(isSilentAiFallbackError(err) ? null : message);
            setUsedAi(false);
            rows = extractLocal();
          }
        } else {
          setUsedAi(false);
          rows = extractLocal();
        }
      }
      // Remote providers may omit host-only fields. Apply composer
      // defaults after extraction so both remote and local paths submit
      // the same group/reminder semantics.
      const withDefaults = rows.map((r) => {
        const defaults = resolveKindDefaults(kindBehavior, r.kind);
        const shouldDefaultEnd =
          r.endsAt === undefined &&
          !r.allDay &&
          defaults.durationMinutes > 0 &&
          !/^\d{4}-\d{2}-\d{2}$/.test(r.startsAt);
        return {
          ...r,
          ...(shouldDefaultEnd
            ? { endsAt: plusMinutes(r.startsAt, defaults.durationMinutes) }
            : {}),
          ...(defaultGroupId !== null && r.groupId === null
            ? { groupId: defaultGroupId }
            : {}),
          ...(remindersEnabled
            ? {
                reminderMinutesBefore:
                  r.reminderMinutesBefore !== undefined
                    ? r.reminderMinutesBefore
                    : defaultReminderMinutes === KIND_DEFAULT_REMINDER_VALUE
                      ? defaults.reminderMinutesBefore
                      : defaultReminderMinutes,
              }
            : {}),
        };
      });
      setExtracted(
        recurrenceEnabled
          ? withDefaults
          : withDefaults.map((r) => ({ ...r, recurring: false, recurrence: null })),
      );
    } finally {
      setLoading(false);
    }
  }, [
    text,
    aiExtract,
    defaultGroupId,
    kindKeys,
    viewerTz,
    kindBehavior,
    recurrenceEnabled,
    remindersEnabled,
    defaultReminderMinutes,
  ]);

  // Auto-run once on mount, but ONLY when the local heuristic extractor
  // is in play. With a real `aiExtract` prop the call costs paid LLM
  // tokens — we wait for an explicit user click on "Extract" instead.
  useEffect(() => {
    if (aiExtract !== undefined) return;
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const kept = (extracted ?? []).filter((_, i) => !removed.has(i));

  const submit = (): void => { onSubmitBatch(kept); };

  const groupOptions: readonly SelectOption[] = useMemo(
    () => buildDefaultGroupOptions(groups, messages.composerPrimaryGroupNone),
    [groups, messages.composerPrimaryGroupNone],
  );
  const reminderOptions: readonly SelectOption[] = useMemo(
    () => buildReminderOptions(messages, { includeKindDefault: true }),
    [messages],
  );

  return (
    <>
      <div className="vq-cal__composer-scroll">
        <div className="vq-cal__composer-workspace">
          <div className="vq-cal__composer-workspace-form">
            <Section title={messages.composerSectionPasteAi}>
              <Textarea
                value={text}
                onChange={(e) => { setText(e.target.value); }}
                rows={mobile ? 6 : 8}
              />
              <div className="vq-cal__composer-batch-bar">
                {/* Always render the "Default group" picker — `groupOptions`
                    carries the "Private" sentinel even when the viewer has
                    no groups, so the affordance stays consistent across
                    modes. The sentinel maps back to `null`. */}
                <span className="vq-cal__composer-batch-default">
                  <span className="vq-cal__composer-batch-default-label">
                    {messages.composerDefaultGroupLabel}
                  </span>
                  <Select
                    options={groupOptions}
                    value={defaultGroupId ?? PRIVATE_GROUP_SENTINEL}
                    onValueChange={(v) => {
                      setDefaultGroupId(v === PRIVATE_GROUP_SENTINEL ? null : v);
                    }}
                    size="sm"
                  />
                </span>
                {remindersEnabled && (
                  <span className="vq-cal__composer-batch-default">
                    <span className="vq-cal__composer-batch-default-label">
                      {messages.composerDefaultReminderLabel}
                    </span>
                    <Select
                      options={reminderOptions}
                      value={
                        defaultReminderMinutes === KIND_DEFAULT_REMINDER_VALUE
                          ? KIND_DEFAULT_REMINDER_VALUE
                          : defaultReminderMinutes === null
                            ? 'none'
                            : String(defaultReminderMinutes)
                      }
                      onValueChange={(value) => {
                        setDefaultReminderMinutes(
                          value === KIND_DEFAULT_REMINDER_VALUE
                            ? KIND_DEFAULT_REMINDER_VALUE
                            : value === 'none'
                              ? null
                              : Number(value),
                        );
                      }}
                      size="sm"
                    />
                  </span>
                )}
                <span className="vq-cal__composer-batch-stats">
                  {usedAi && extracted !== null && aiError === null && (
                    <Tag tone="accent" size="sm">
                      {messages.composerAiPoweredTag}
                    </Tag>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    shape="pill"
                    loading={loading}
                    onClick={() => void run()}
                  >
                    {loading
                      ? messages.composerReading
                      : extracted === null
                        ? messages.composerExtract
                        : messages.composerReExtract}
                  </Button>
                </span>
              </div>
              {aiError !== null && extracted !== null && (
                <p className="vq-cal__composer-error" role="status">
                  <IcnAlert size={12} />
                  <span>
                    {formatMessage(messages.composerAiFallbackNote, {
                      reason: aiError.length > 120 ? aiError.slice(0, 120) + '…' : aiError,
                    })}
                  </span>
                </p>
              )}
            </Section>
          </div>

          <aside className="vq-cal__composer-preview" aria-live="polite">
            <header className="vq-cal__composer-preview-head">
              <span>{messages.composerSectionSuggested}</span>
            </header>
            <SuggestedList
              extracted={extracted}
              loading={loading}
              removed={removed}
              kindTheme={kindTheme}
              messages={messages}
              onSkip={(i) =>
                { setRemoved((prev) => {
                  const next = new Set(prev);
                  next.add(i);
                  return next;
                }); }
              }
            />
          </aside>
        </div>
      </div>

      <FooterActions
        primary={
          <Button
            variant="primary"
            size="md"
            disabled={kept.length === 0}
            onClick={submit}
          >
            {kept.length === 0
              ? messages.composerAddEventsZero
              : formatMessage(
                  kept.length === 1
                    ? messages.composerAddEventsOne
                    : messages.composerAddEventsOther,
                  { count: kept.length },
                )}
          </Button>
        }
        secondary={
          <Button variant="ghost" size="md" onClick={onCancel}>
            {messages.composerCancel}
          </Button>
        }
        leading={
          extracted !== null ? (
            <Badge tone="neutral" size="sm" numeric>
              {kept.length}
            </Badge>
          ) : null
        }
      />
    </>
  );
}

function SuggestedList(props: {
  extracted: readonly EventComposerSubmitDraft[] | null;
  loading: boolean;
  removed: ReadonlySet<number>;
  kindTheme: CalendarKindMap;
  messages: ReturnType<typeof useCalendarMessages>;
  onSkip: (index: number) => void;
}): ReactNode {
  const { extracted, loading, removed, kindTheme, messages, onSkip } = props;
  if (extracted === null) {
    return (
      <Empty
        size="compact"
        title={
          loading
            ? messages.composerAiLoadingTitle
            : messages.composerAiPromptTitle
        }
        description={
          loading
            ? messages.composerAiLoadingDesc
            : messages.composerAiPromptDesc
        }
      />
    );
  }
  if (extracted.length === 0) {
    return (
      <Empty
        size="compact"
        title={messages.composerAiEmptyTitle}
        description={messages.composerAiEmptyDesc}
      />
    );
  }
  return (
    <List size="sm" bordered={false} aria-label={messages.composerSuggestedListAriaLabel}>
      {extracted.map((e, i) => {
        if (removed.has(i)) return null;
        const def = kindTheme[e.kind] ?? kindTheme.default;
        const color = def?.color ?? 'var(--vq-rule-strong)';
        return (
          <List.Item
            key={i}
            leading={
              <span
                className="vq-cal__composer-batch-bar-color"
                style={{ background: color }}
                aria-hidden="true"
              />
            }
            title={e.title}
            description={
              <span className="vq-cal__composer-batch-row-meta">
                <Tag tone="neutral" size="sm">
                  {e.startsAt.slice(0, 10)} · {e.startsAt.slice(11, 16)}
                </Tag>
                <span
                  className="vq-cal__composer-batch-row-kind"
                  style={{ color }}
                >
                  {def?.label ?? e.kind}
                </span>
                {e.recurring && (
                  <Tag tone="accent" size="sm">
                    {messages.composerAiRepeatsTag}
                  </Tag>
                )}
              </span>
            }
            trailing={
              <Button
                variant="ghost"
                size="sm"
                shape="pill"
                onClick={() => { onSkip(i); }}
              >
                {messages.composerSkip}
              </Button>
            }
          />
        );
      })}
    </List>
  );
}
